import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getHotHoldingBoardQueryControllerGetBoard } from "@/generated/hot-holding-meals/hot-holding-meals";
import type {
  HotHoldingBoardResponseDto,
  HotHoldingMealResponseDto,
  HotHoldingReadingResponseDto,
  HotHoldingReadingResponseDtoReadingType,
} from "@/generated/model";
import {
  cachedUserId,
  generateClientId,
  offlineDb,
  type OfflineHotHoldingMeal,
  type OfflineHotHoldingReading,
} from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

/** v1 hot-holding floor (mirrors the server constant). Used only to flag
 *  locally-pending readings before they sync; the server is authoritative. */
export const HOT_HOLDING_MIN_TEMP_C = 65;

type ReadingType = "DELIVERY" | "SERVICE";

export interface BoardReading extends HotHoldingReadingResponseDto {
  localSyncState?: "pending" | "error";
}
export interface BoardMeal extends Omit<HotHoldingMealResponseDto, "readings"> {
  readings: BoardReading[];
  localSyncState?: "pending" | "error";
}

export interface UseOfflineHotHoldingBoardResult {
  awaitingService: BoardMeal[];
  completed: BoardMeal[];
  isLoading: boolean;
  isOffline: boolean;
  logDelivery: (params: {
    mealName: string;
    temperature: number;
    notes?: string;
  }) => Promise<void>;
  addReading: (params: {
    mealServerId?: string;
    mealLocalId?: string;
    readingType: ReadingType;
    temperature: number;
    notes?: string;
  }) => Promise<void>;
  refetch: () => Promise<void>;
}

const isOutOfRange = (temperature: number) =>
  Number.isFinite(temperature) && temperature < HOT_HOLDING_MIN_TEMP_C;

/**
 * Offline-first hook for the hot holding board (NAU-93). Reads the server board
 * when online and merges in locally-pending meals/readings so a submission made
 * offline shows immediately and survives until it syncs. Writes always go to
 * IndexedDB + the sync queue (works on or offline); the two-step delivery →
 * service flow reconciles the meal's server id on sync (see syncService).
 */
export function useOfflineHotHoldingBoard(
  nurseryId: string | null,
): UseOfflineHotHoldingBoardResult {
  const [serverBoard, setServerBoard] =
    useState<HotHoldingBoardResponseDto | null>(null);
  const [localMeals, setLocalMeals] = useState<OfflineHotHoldingMeal[]>([]);
  const [localReadings, setLocalReadings] = useState<
    OfflineHotHoldingReading[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const nurseryIdRef = useRef(nurseryId);
  nurseryIdRef.current = nurseryId;

  const loadLocal = useCallback(async () => {
    if (!nurseryId) return;
    const meals = await offlineDb.hotHoldingMeals
      .filter((m) => m.nurseryId === nurseryId && m.syncState !== "synced")
      .toArray();
    const readings = await offlineDb.hotHoldingReadings
      .filter((r) => r.nurseryId === nurseryId && r.syncState !== "synced")
      .toArray();
    setLocalMeals(meals);
    setLocalReadings(readings);
  }, [nurseryId]);

  const fetchFromServer = useCallback(async () => {
    if (!nurseryId || !navigator.onLine) return;
    try {
      const res = await getHotHoldingBoardQueryControllerGetBoard(nurseryId);
      setServerBoard(
        (res.data ?? res) as unknown as HotHoldingBoardResponseDto,
      );
    } catch (err) {
      console.error("[OfflineHotHolding] Failed to fetch board:", err);
      setIsOffline(true);
    }
  }, [nurseryId]);

  useEffect(() => {
    if (!nurseryId) {
      setIsLoading(false);
      return;
    }
    (async () => {
      setIsLoading(true);
      await loadLocal();
      if (navigator.onLine) await fetchFromServer();
      setIsLoading(false);
    })();
  }, [nurseryId, loadLocal, fetchFromServer]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      fetchFromServer();
    };
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [fetchFromServer]);

  // Refetch + drop synced locals when a sync run finishes.
  useEffect(() => {
    let wasSyncing = false;
    const unsubscribe = syncService.subscribe((status: SyncStatus) => {
      if (wasSyncing && !status.isSyncing && navigator.onLine) {
        fetchFromServer();
        loadLocal();
      }
      wasSyncing = status.isSyncing;
    });
    return () => {
      unsubscribe();
    };
  }, [fetchFromServer, loadLocal]);

  const cachedUserName = useCallback(async () => {
    const session = await offlineDb.userSession.get("current-user");
    if (!session) return "You";
    return (
      [session.firstName, session.lastName].filter(Boolean).join(" ") ||
      session.email ||
      "You"
    );
  }, []);

  const logDelivery = useCallback(
    async (params: {
      mealName: string;
      temperature: number;
      notes?: string;
    }) => {
      const nid = nurseryIdRef.current;
      if (!nid) return;
      const recordedByName = await cachedUserName();
      const capturedByUserId = await cachedUserId();
      const now = new Date().toISOString();
      const mealLocalId = generateClientId();

      await offlineDb.hotHoldingMeals.add({
        id: mealLocalId,
        nurseryId: nid,
        mealName: params.mealName,
        timestamp: now,
        recordedByName,
        capturedByUserId,
        syncState: "pending",
      });
      await offlineDb.hotHoldingReadings.add({
        id: generateClientId(),
        nurseryId: nid,
        mealLocalId,
        readingType: "DELIVERY",
        temperature: params.temperature,
        notes: params.notes,
        isFirstReading: true,
        timestamp: now,
        recordedByName,
        capturedByUserId,
        syncState: "pending",
      });
      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "hotHoldingMeal",
        entityId: mealLocalId,
        priority: 3,
        createdAt: Date.now(),
        attempts: 0,
      });

      await loadLocal();
      if (navigator.onLine) syncService.debouncedSync();
    },
    [cachedUserName, loadLocal],
  );

  const addReading = useCallback(
    async (params: {
      mealServerId?: string;
      mealLocalId?: string;
      readingType: ReadingType;
      temperature: number;
      notes?: string;
    }) => {
      const nid = nurseryIdRef.current;
      if (!nid) return;
      const recordedByName = await cachedUserName();
      const capturedByUserId = await cachedUserId();
      const readingLocalId = generateClientId();

      await offlineDb.hotHoldingReadings.add({
        id: readingLocalId,
        nurseryId: nid,
        mealServerId: params.mealServerId,
        mealLocalId: params.mealLocalId,
        readingType: params.readingType,
        temperature: params.temperature,
        notes: params.notes,
        isFirstReading: false,
        timestamp: new Date().toISOString(),
        recordedByName,
        capturedByUserId,
        syncState: "pending",
      });
      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "hotHoldingReading",
        entityId: readingLocalId,
        // Lower priority than the meal so a same-session meal syncs first.
        priority: 4,
        createdAt: Date.now(),
        attempts: 0,
      });

      await loadLocal();
      if (navigator.onLine) syncService.debouncedSync();
    },
    [cachedUserName, loadLocal],
  );

  const refetch = useCallback(async () => {
    await Promise.all([fetchFromServer(), loadLocal()]);
  }, [fetchFromServer, loadLocal]);

  const { awaitingService, completed } = useMemo(
    () => mergeBoard(serverBoard, localMeals, localReadings),
    [serverBoard, localMeals, localReadings],
  );

  return {
    awaitingService,
    completed,
    isLoading,
    isOffline,
    logDelivery,
    addReading,
    refetch,
  };
}

/** Combine the server board with locally-pending rows into the two lanes. */
function mergeBoard(
  serverBoard: HotHoldingBoardResponseDto | null,
  localMeals: OfflineHotHoldingMeal[],
  localReadings: OfflineHotHoldingReading[],
): { awaitingService: BoardMeal[]; completed: BoardMeal[] } {
  const localReadingToBoard = (r: OfflineHotHoldingReading): BoardReading => ({
    id: r.id,
    readingType: r.readingType as HotHoldingReadingResponseDtoReadingType,
    temperature: r.temperature,
    isOutOfRange: isOutOfRange(r.temperature),
    notes: r.notes ?? null,
    recordedAt: r.timestamp,
    recordedBy: {
      id: "",
      firstName: r.recordedByName,
      lastName: null,
      email: "",
    },
    localSyncState: r.syncState === "error" ? "error" : "pending",
  });

  const serverMeals: BoardMeal[] = [
    ...(serverBoard?.awaitingService ?? []),
    ...(serverBoard?.completed ?? []),
  ].map((m) => ({ ...m, readings: [...m.readings] }));

  // Attach pending readings added to already-synced server meals.
  for (const r of localReadings) {
    if (!r.mealServerId) continue;
    const target = serverMeals.find((m) => m.id === r.mealServerId);
    if (target) target.readings.push(localReadingToBoard(r));
  }

  // Pending local meals (not yet synced) with their own pending readings.
  const pendingMeals: BoardMeal[] = localMeals.map((m) => ({
    id: m.id,
    mealName: m.mealName,
    isComplete: false,
    localSyncState: m.syncState === "error" ? "error" : "pending",
    readings: localReadings
      .filter((r) => r.mealLocalId === m.id)
      .map(localReadingToBoard),
  }));

  const finalize = (meal: BoardMeal): BoardMeal => {
    const readings = [...meal.readings].sort(
      (a, b) =>
        new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime(),
    );
    const isComplete =
      readings.some((r) => r.readingType === "DELIVERY") &&
      readings.some((r) => r.readingType === "SERVICE");
    return { ...meal, readings, isComplete };
  };

  const all = [...serverMeals, ...pendingMeals].map(finalize);
  return {
    awaitingService: all.filter((m) => !m.isComplete),
    completed: all.filter((m) => m.isComplete),
  };
}
